/**
 * pastoralIsolationAwareness.js
 *
 * Infers pastoral isolation — the pastor carrying the work alone,
 * without peer accompaniment, without anyone who truly understands.
 *
 * DISTINCT FROM:
 *   pastoralLoadDistribution (FASE 10) → structural work distribution
 *   influenceAndCareNetwork (FASE 10)  → care imbalance around the pastor
 * THIS: the PASTOR'S felt experience of isolation — loneliness in the calling,
 *       absence of peer support, carrying weight with no one to share it with.
 *
 * States:
 *   acompañado           — adequate support present
 *   soledad_leve         — some isolation, but not deep
 *   aislamiento_pastoral — meaningful pastoral loneliness
 *   aislamiento_profundo — deep isolation; no peer support, no understanding
 */

const ISOLATION_SIGNALS = [
  // "Nobody understands" language (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:nadie\s+(?:entiende|comprende)\s+(?:lo\s+que\s+(?:es|significa|implica)|(?:cómo|qué\s+es)\s+ser)\s+(?:pastor|líder|ministro))/i,
      /(?:es\s+(?:muy\s+)?(?:difícil|solitario)\s+(?:hablar\s+(?:con\s+alguien|de\s+esto)|encontrar\s+(?:a\s+alguien|con\s+quién)))/i,
      /(?:no\s+tengo\s+(?:con\s+quién|a\s+quién)\s+(?:hablar|contarle|compartir\s+esto|ser\s+vulnerable))/i,
    ],
  },
  // Carrying alone explicitly stated (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:cargo\s+(?:esto|todo|esta\s+carga)\s+(?:solo|a\s+solas|en\s+silencio))/i,
      /(?:nadie\s+(?:sabe\s+por\s+lo\s+que\s+paso|me\s+apoya|me\s+acompaña|me\s+respalda))/i,
      /(?:me\s+siento\s+(?:muy\s+)?solo\s+(?:en\s+el\s+ministerio|en\s+esto|en\s+mi\s+vocación))/i,
    ],
  },
  // No pastoral peer support (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:no\s+(?:tengo|cuento\s+con)\s+(?:otros\s+pastores?|colegas?|hermanos?\s+(?:en\s+el\s+ministerio|pastorales?))\s+(?:con\s+quienes?|a\s+quienes?\s+(?:acudir|hablar)))/i,
      /(?:estoy\s+(?:muy\s+)?aislado\s+(?:de\s+(?:otros\s+pastores?|colegas?|mis\s+pares?)|en\s+mi\s+ministerio))/i,
    ],
  },
  // Self-sufficiency pattern from isolation (weight: 1)
  {
    weight: 1,
    patterns: [
      /(?:aprendí\s+a\s+(?:hacerlo|seguir|ministrar)\s+solo|ya\s+no\s+(?:busco|espero|pido)\s+apoyo)/i,
      /(?:no\s+tiene\s+sentido\s+(?:compartirlo|decírselo|pedirlo)\s+(?:a\s+nadie|porque\s+nadie\s+(?:entiende|puede\s+ayudar)))/i,
    ],
  },
];

/**
 * @param {{
 *   text:            string,
 *   emotion:         object|null,
 *   exhaustionState: string,
 *   entityContext:   object|null,
 * }} params
 * @returns {{
 *   isolationState: string,
 *   score:          number,
 *   instruction:    string|null,
 * }}
 */
export function detectPastoralIsolation({
  text            = "",
  emotion         = null,
  exhaustionState = "estable",
  entityContext   = null,
}) {
  const t = text ?? "";
  let score = 0;

  for (const { weight, patterns } of ISOLATION_SIGNALS) {
    if (patterns.some((p) => p.test(t))) score += weight;
  }

  // Exhaustion amplifies isolation risk
  if (exhaustionState === "desgaste_profundo" || exhaustionState === "saturación_crítica") score += 1;

  const isolationState =
    score >= 5 ? "aislamiento_profundo"  :
    score >= 3 ? "aislamiento_pastoral"  :
    score >= 1 ? "soledad_leve"          :
    "acompañado";

  return {
    isolationState,
    score: Math.round(score * 10) / 10,
    instruction: _instruction(isolationState),
  };
}

function _instruction(state) {
  if (state === "aislamiento_profundo") {
    return "El pastor está profundamente aislado. Kevia es presencia, no solución — acompaña desde la cercanía sin pretender reemplazar la comunidad pastoral que necesita.";
  }
  if (state === "aislamiento_pastoral") {
    return "Hay aislamiento pastoral significativo. Favorece la conexión con pares y la búsqueda de apoyo sin presión.";
  }
  if (state === "soledad_leve") {
    return "Hay soledad pastoral presente. Valida esa carga sin minimizarla.";
  }
  return null;
}
